import React, {
	SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import {
	more,
	redo as redoIcon,
	undo as undoIcon,
} from '@/assets/icons';
import { BottomCardConfig, ILayoutProps } from '@/components/Layout';
import { getZonedStartDateTimeISO } from '@/components/Layout/components/CreateSetlistPanel';
import { GenerateSettingsBottomPanel } from '@/components/Layout/components/GenerateSettingsBottomPanel/GenerateSettingsBottomPanel';
import {
	SetlistServiceTitle,
	SetlistStatus,
	SetlistSummaryCalendar,
} from '@/components/SetlistSummaryCard';
import {
	getSetlistStatus,
	SetlistStatusEnum,
} from '@/components/SetlistSummaryCard/utils';
import {
	ActionSheet,
	ActionSheetAction,
} from '@/components/shared/ActionSheet';
import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import {
	AsaphDialog,
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import { Flex } from '@/components/shared/Flex/Flex';
import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import { ModalSectionTitle } from '@/components/shared/ModalSectionTitle';
import { SmallSongCardButton } from '@/components/shared/SmallSongCardButton';
import { Tabs } from '@/components/shared/Tabs';
import { PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import {
	GenerateSetlistRequestInput,
	Permissions,
	SetlistTeamSong,
	useClearGenerateSetlistCacheMutation,
	useCreateSetlistMutation,
	useDeleteSetlistMutation,
	useGenerateSetlistMutation,
	useGetBiblePassageAnalysisMutation,
	useListMySetlistFeedbackRequestsQuery,
	useUpdateSetlistMutation,
} from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { inRange } from '@/helpers/inRange';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { FeedbackPanel } from '@/pages/Dashboard/components/FeedbackPanel';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { SetlistsContext, useSetlists } from '@/providers/SetlistsProvider';
import { useTeam } from '@/providers/TeamProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import constants from '@/theme/exportedVariables.module.scss';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import {
	closestCenter,
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	restrictToParentElement,
	restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { IconButton, SvgIcon, useMediaQuery } from '@material-ui/core';
import { Day, getDay, nextDay } from 'date-fns';
import { useSnackbar } from 'notistack';

import { SongAnalysis } from '../SongAnalysisPanel/SongAnalysis';
import ActionContent from './components/ActionContent/ActionContent';
import { SlotActionRow } from './components/ActionContent/SlotActionRow';
import { DefaultKeyBottomCard } from './components/DefaultKeyBottomCard/DefaultKeyBottomCard';
import { PassageChipPanel } from './components/PassageChipPanel/PassageChipPanel';
import { ServiceChipPanel } from './components/ServiceChipPanel/ServiceChipPanel';
import SetlistAnalysis from './components/SetlistAnalysis/SetlistAnalysis';
import { SetlistChipRow } from './components/SetlistChipRow/SetlistChipRow';
import { SetlistSummary } from './components/SetlistSummary';
import ShareDialog from './components/ShareDialog/ShareDialog';
import { SongsChipPanel } from './components/SongsChipPanel/SongsChipPanel';
import { SortableItem } from './components/SortableItem/SortableItem';
import styles from './GenerateSetlist.module.scss';
import {
	areSnapshotsEqual,
	SetlistVersionSnapshot,
	useSetlistVersionStack,
} from './hooks/useSetlistVersionStack';

export type InitialSetlistGenerateSettings = {
	totalSongPositions?: number;
	postSermonSongPosition?: number | null;
	searchTerm?: string;
	bibleReference?: string;
	serviceId?: string | null;
	startTime?: string;
	startDate?: string;
};

export type RequiredSetlistGenerateSettings = Omit<
	InitialSetlistGenerateSettings,
	'totalSongPositions' | 'startDate' | 'startTime'
> & {
	totalSongPositions: NonNullable<
		InitialSetlistGenerateSettings['totalSongPositions']
	>;
	startDate: NonNullable<InitialSetlistGenerateSettings['startDate']>;
	startTime: NonNullable<InitialSetlistGenerateSettings['startTime']>;
};

const isCapacitor = Capacitor.isNativePlatform();

type GenerateSetlistContext = {
	open: boolean;
	anchorEl: Element | null;
	selectedSongIds: string[];
	songOverrides: SongOverride[];
	isGenerateLoading: boolean;
	setlistId?: string;
	isEdit: boolean;
	isReorderMode: boolean;
	setlist: SetlistsContext['setlists'][0] | undefined;
	status: SetlistStatusEnum;
	lockedTeamSongIds: string[];
	generateSettings: RequiredSetlistGenerateSettings;
	fromOnboarding?: boolean;
	versionLabel: string;
	canUndo: boolean;
	canRedo: boolean;
	setFeedbackSetlistId: React.Dispatch<React.SetStateAction<string | null>>;
	setIsSettingsPanelOpen: (isOpen: boolean) => void;
	handleDropdownClose: () => void;
	generateSetlist: () => void;
	saveDraftSetlist: (draft: boolean) => void;
	setIsEdit: (isEdit: boolean) => void;
	setIsReorderMode: (isReorderMode: boolean) => void;
	handleDeleteDialogOpen: () => void;
	setShareOpen: React.Dispatch<React.SetStateAction<boolean>>;
	duplicateSetlist: () => void;
	undoVersion: () => void;
	redoVersion: () => void;
};

export type SongOverride = {
	id: string;
	defaultKey?: string | null;
};

export const GenerateSetlistContext =
	React.createContext<GenerateSetlistContext>({
		open: false,
		anchorEl: null,
		isGenerateLoading: false,
		selectedSongIds: [],
		songOverrides: [],
		setlistId: undefined,
		isEdit: false,
		isReorderMode: false,
		setlist: undefined,
		status: SetlistStatusEnum.EDITING,
		lockedTeamSongIds: [],
		generateSettings: {
			startDate: new Date().toISOString(),
			startTime: new Date().toISOString(),
			totalSongPositions: 0,
		},
		fromOnboarding: false,
		versionLabel: '1',
		canUndo: false,
		canRedo: false,
		setFeedbackSetlistId: () => {},
		setIsSettingsPanelOpen: () => {},
		handleDropdownClose: () => {},
		generateSetlist: () => {},
		saveDraftSetlist: () => {},
		setIsEdit: () => {},
		setIsReorderMode: () => {},
		handleDeleteDialogOpen: () => {},
		setShareOpen: () => {},
		duplicateSetlist: () => {},
		undoVersion: () => {},
		redoVersion: () => {},
	});
export interface GenerateSetlistProps {
	setOpen: (open: boolean) => void;
	config: {
		songOverrides?: SongOverride[];
		generateSettings: RequiredSetlistGenerateSettings;
		setlistId?: string;
		selectedSongIds?: string[];
		generateOnOpen?: boolean;
		editOnOpen?: boolean;
		fromOnboarding?: boolean;
	};
	openSonglistWithCallback: ILayoutProps['openSonglistWithCallback'];
	openSong: ILayoutProps['openSong'];
	setAccountOpen: ILayoutProps['setAccountOpen'];
	callbackFn?: (res: {
		setlistId?: string;
		songsLength?: number;
		removed?: boolean;
		savedDraft?: boolean;
		fromOnboarding?: boolean;
		submittedFeedback?: boolean;
	}) => void;
	openBottomCard?: (config: BottomCardConfig) => void;
}

export const GenerateSetlist: React.FC<GenerateSetlistProps> = ({
	setOpen,
	callbackFn,
	openSonglistWithCallback,
	openSong,
	openBottomCard,
	setAccountOpen,
	config,
}) => {
	const { appendMilestone, hasMilestones } = useAuth();
	const { team } = useTeam();
	const { hasPermission } = usePermissions();
	const { refetch, setlists } = useSetlists();
	const { handleError } = useErrorHandler();
	const { openSetlistWithCallback } = useLayout();
	const { enqueueSnackbar } = useSnackbar();
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);
	const isDesktop = useMediaQuery(
		`(min-width: ${constants.sizeMediumDesktop}`,
	);

	const {
		generateSettings: initialGenerateSettings,
		selectedSongIds: initialSelectedSongIds,
		songOverrides: initialSongOverrides,
		generateOnOpen = false,
		editOnOpen = false,
		fromOnboarding = false,
	} = config;

	const [selectedTab, setSelectedTab] = useState(0);
	const [anchorEl, setAnchorEl] = useState<Element | null>(null);
	const [generateSettings, setGenerateSettings] = useState(
		initialGenerateSettings,
	);
	const [isEdit, setIsEdit] = useState(editOnOpen);
	const [isDragging, setIsDragging] = useState(false);
	const [isReorderMode, setIsReorderMode] = useState(false);
	const [isWarningOpen, setIsWarningOpen] = useState<
		React.ReactNode | undefined
	>();
	const [selectedSongIds, setSelectedSongIds] = useState<string[]>(
		initialSelectedSongIds ||
			Array.from(
				{ length: initialGenerateSettings.totalSongPositions },
				(_, i) => `song-${i + 1}`,
			),
	);
	const [songOverrides, setSongOverrides] = useState<SongOverride[]>(
		initialSongOverrides || [],
	);
	const {
		versionLabel,
		canUndo,
		canRedo,
		commitVersion,
		commitEditVersion,
		commitGenerate,
		markDirty,
		undo,
		redo,
		resetVersions,
	} = useSetlistVersionStack({
		selectedSongIds:
			initialSelectedSongIds ||
			Array.from(
				{ length: initialGenerateSettings.totalSongPositions },
				(_, i) => `song-${i + 1}`,
			),
		totalSongPositions: initialGenerateSettings.totalSongPositions,
		postSermonSongPosition: initialGenerateSettings.postSermonSongPosition,
	});
	const generateSettingsRef = useRef(generateSettings);
	generateSettingsRef.current = generateSettings;
	const reorderEntrySnapshotRef = useRef<SetlistVersionSnapshot | null>(
		null,
	);
	const isInitialGenerateRef = useRef(false);
	const currentSnapshot = useCallback(
		(): SetlistVersionSnapshot => ({
			selectedSongIds,
			totalSongPositions: generateSettings.totalSongPositions,
			postSermonSongPosition: generateSettings.postSermonSongPosition,
		}),
		[selectedSongIds, generateSettings],
	);
	const applyVersionSnapshot = useCallback(
		(snapshot: SetlistVersionSnapshot) => {
			setSelectedSongIds(snapshot.selectedSongIds);
			setGenerateSettings((s) => ({
				...s,
				totalSongPositions: snapshot.totalSongPositions,
				postSermonSongPosition: snapshot.postSermonSongPosition,
			}));
		},
		[],
	);
	const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);
	const [postOpenSetlistId, postOpenSetSetlistId] = useState<
		string | undefined
	>(config.setlistId);
	const setlistId = postOpenSetlistId || config.setlistId;
	const [generatedOnOpen, setGeneratedOnOpen] = useState(false);
	const [feedbackSetlistId, setFeedbackSetlistId] = useState<string | null>(
		null,
	);
	const [shareOpen, setShareOpen] = useState(false);
	const [isServicePanelOpen, setIsServicePanelOpen] = useState(false);
	const [isSongsPanelOpen, setIsSongsPanelOpen] = useState(false);
	const [isPassagePanelOpen, setIsPassagePanelOpen] = useState(false);
	const [defaultKeyPanelOpen, setDefaultKeyPanelOpen] = useState<{
		teamSongId: string;
	} | null>(null);
	const [reasoning, setReasoning] = useState<object>();
	const [lastGeneratePositions, setLastGeneratePositions] =
		useState<object>();
	const [lockedTeamSongIds, setLockedTeamSongIds] = useState<string[]>([]);
	const [submittedFeedback, setSubmittedFeedback] = useState(false);

	const setlist = useMemo(() => {
		return setlists.find((s) => s.id === setlistId);
	}, [setlistId, setlists]);

	const status = useMemo(() => {
		const { status } = getSetlistStatus(setlist, isEdit);
		return status;
	}, [setlist, isEdit]);

	const handleDeleteDialogOpen = () => {
		setIsDeleteOpen(true);
	};
	const handleDeleteDialogClose = () => {
		setIsDeleteOpen(false);
	};

	const {
		data: biblePassageData,
		isLoading: isBibleVerseLoading,
		mutateAsync: getBibleVerse,
	} = useGetBiblePassageAnalysisMutation();
	const {
		mutateAsync: generateSetlistMutation,
		isLoading: isGenerateLoading,
	} = useGenerateSetlistMutation({
		onSuccess: (generatedSetlistData) => {
			const nextIds = generatedSetlistData.generateSetlist.positions
				.filter((position) => !!position)
				.map((position) => position!.id);
			setSelectedSongIds(nextIds);
			setReasoning(generatedSetlistData.generateSetlist.reasoning);

			const snapshot: SetlistVersionSnapshot = {
				selectedSongIds: nextIds,
				totalSongPositions:
					generateSettingsRef.current.totalSongPositions,
				postSermonSongPosition:
					generateSettingsRef.current.postSermonSongPosition,
			};
			if (isInitialGenerateRef.current) {
				isInitialGenerateRef.current = false;
				resetVersions(snapshot);
			} else {
				commitGenerate(snapshot, lockedTeamSongIds.length > 0);
			}
		},
		onError: () => {
			isInitialGenerateRef.current = false;
		},
	});
	const { mutateAsync: clearGenerateSetlistCacheMutation } =
		useClearGenerateSetlistCacheMutation();
	const { mutateAsync: deleteAsync } = useDeleteSetlistMutation();
	const { mutateAsync: createAsync } = useCreateSetlistMutation();
	const { mutateAsync: updateAsync } = useUpdateSetlistMutation();
	const { refetch: refetchFeedback } =
		useListMySetlistFeedbackRequestsQuery();

	const fireAddSongsEvent = useAnalyticsEvent('Generate_Setlist_Add_Songs');
	const fireRegenerateEvent = useAnalyticsEvent(
		'Generate_Setlitst_Regenerate',
	);
	const fireViewTab = useAnalyticsEvent('Generate_Setlist_View_Tab');
	const fireEnterReorderMode = useAnalyticsEvent(
		'Generate_Setlist_Enter_Reorder_Mode',
	);
	const fireExitReorderMode = useAnalyticsEvent(
		'Generate_Setlist_Exit_Reorder_Mode',
	);
	const fireUndo = useAnalyticsEvent('Generate_Setlist_Undo');
	const fireRedo = useAnalyticsEvent('Generate_Setlist_Redo');

	const undoVersion = useCallback(() => {
		const snapshot = undo();
		if (!snapshot) {
			return;
		}
		fireUndo();
		applyVersionSnapshot(snapshot);
	}, [undo, fireUndo, applyVersionSnapshot]);

	const redoVersion = useCallback(() => {
		const snapshot = redo();
		if (!snapshot) {
			return;
		}
		fireRedo();
		applyVersionSnapshot(snapshot);
	}, [redo, fireRedo, applyVersionSnapshot]);

	const generateSetlist = useCallback(() => {
		fireRegenerateEvent();
		const positions = generatePositions({
			totalPositionsNumber: generateSettings.totalSongPositions,
			postSermonSongPosition: generateSettings.postSermonSongPosition,
			searchTerm: generateSettings.searchTerm,
			bibleReference: generateSettings.bibleReference,
			lockedTeamSongIds,
			currentTeamSongIds: selectedSongIds,
		});
		setLastGeneratePositions(positions);
		generateSetlistMutation({
			teamId: team.id,
			request: {
				positions,
			},
		});
	}, [
		generateSetlistMutation,
		fireRegenerateEvent,
		generateSettings,
		team.id,
		lockedTeamSongIds,
		selectedSongIds,
	]);

	useEffect(() => {
		if (generateOnOpen && !generatedOnOpen) {
			isInitialGenerateRef.current = true;
			generateSetlist();
			setGeneratedOnOpen(true);
		}
	}, [generateSetlist, generateOnOpen, generatedOnOpen]);

	useEffect(() => {
		if (generateSettings.bibleReference) {
			getBibleVerse({
				reference: generateSettings.bibleReference,
			});
		}
	}, [generateSettings.bibleReference, getBibleVerse]);

	const handleClose = useCallback<
		NonNullable<GenerateSetlistProps['callbackFn']>
	>(
		({ setlistId, songsLength, removed, savedDraft, fromOnboarding }) => {
			clearGenerateSetlistCacheMutation({});
			setOpen(false);
			callbackFn?.({
				setlistId,
				songsLength,
				removed,
				savedDraft,
				submittedFeedback,
				fromOnboarding,
			});
		},
		[
			setOpen,
			clearGenerateSetlistCacheMutation,
			callbackFn,
			submittedFeedback,
		],
	);

	// const handleClose = useCallback(() => {
	// 	clearGenerateSetlistCacheMutation({});
	// 	setOpen(false);
	// 	callbackFn?.({
	// 		removed: false,
	// 		savedDraft: false,
	// 		setlistId: undefined,
	// 		songsLength: undefined,
	// 	});
	// }, [setOpen, clearGenerateSetlistCacheMutation, callbackFn]);

	const handleAddSongs = useCallback(() => {
		openSonglistWithCallback(
			(songIds, navigatedAway) => {
				if (songIds.length) {
					const nextIds = [...selectedSongIds, ...songIds];
					const nextTotal =
						generateSettings.totalSongPositions + songIds.length;
					setSelectedSongIds(nextIds);
					setGenerateSettings((settings) => ({
						...settings,
						totalSongPositions: nextTotal,
					}));
					commitVersion({
						selectedSongIds: nextIds,
						totalSongPositions: nextTotal,
						postSermonSongPosition:
							generateSettings.postSermonSongPosition,
					});
					fireAddSongsEvent();
				}
				if (navigatedAway) {
					handleClose({});
				}
			},
			{
				actionLabelVerb: 'Select',
				title: 'Select setlist songs',
				excludeTeamSongIds: selectedSongIds,
			},
		);
	}, [
		openSonglistWithCallback,
		selectedSongIds,
		generateSettings,
		commitVersion,
		fireAddSongsEvent,
		handleClose,
	]);

	const saveDraftSetlist = async (draft: boolean) => {
		try {
			const input = {
				startAt: getZonedStartDateTimeISO(
					generateSettings.startTime,
					generateSettings.startDate,
				),
				teamSongIds: selectedSongIds,
				teamSongOverrides: songOverrides,
				serviceId: generateSettings.serviceId,
				draft,
				generateSettings: {
					totalSongPositions: generateSettings.totalSongPositions,
					postSermonSongPosition:
						generateSettings.postSermonSongPosition,
					searchTerm: generateSettings.searchTerm,
					bibleReference: generateSettings.bibleReference,
				},
			};
			if (setlistId) {
				await updateAsync({
					setlistId,
					input,
				});
			} else {
				const setlist = await createAsync({
					input,
				});
				postOpenSetSetlistId(setlist.createSetlist.id);
				if (fromOnboarding) {
					handleClose({
						setlistId: setlist.createSetlist.id,
						songsLength: selectedSongIds.length,
						fromOnboarding,
					});
				}
			}
			refetch();
			refetchFeedback();
			setIsEdit(false);
			setIsReorderMode(false);
			setLockedTeamSongIds([]);
			resetVersions(currentSnapshot());
		} catch (error) {
			handleError({
				errorCode: 'SetlistDraftError',
				error,
				friendlyMessage: `There was an issue with saving your draft`,
			});
		}
	};

	const deleteSetlist = async () => {
		if (!setlistId) {
			return handleClose({ removed: true });
		}
		await deleteAsync({ setlistId });
		refetch();
		enqueueSnackbar('Setlist deleted');
		handleClose({ removed: true });
	};

	const duplicateSetlist = () => {
		if (!setlist) return;
		const originalDate = new Date(setlist.startAt);
		const next = nextDay(new Date(), getDay(originalDate) as Day);
		next.setHours(
			originalDate.getHours(),
			originalDate.getMinutes(),
			originalDate.getSeconds(),
			0,
		);
		const gs = setlist.generateSettings;
		handleClose({});
		setTimeout(() => {
			openSetlistWithCallback(() => {}, {
				editOnOpen: true,
				selectedSongIds: setlist.teamSongIds,
				generateSettings: {
					startDate: next.toISOString(),
					startTime: `${String(originalDate.getHours()).padStart(
						2,
						'0',
					)}:${String(originalDate.getMinutes()).padStart(2, '0')}`,
					serviceId: setlist.serviceId ?? undefined,
					totalSongPositions:
						gs?.totalSongPositions ?? setlist.teamSongIds.length,
					postSermonSongPosition:
						gs?.postSermonSongPosition ?? undefined,
					searchTerm: gs?.searchTerm ?? '',
					bibleReference:
						gs?.bibleReference ?? gs?.bibleVerseReference ?? '',
				},
			});
		}, 0);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		setIsDragging(false);

		if (over && active.id !== over.id) {
			setSelectedSongIds((items) => {
				const oldIndex = items.indexOf(active.id as string);
				const newIndex = items.indexOf(over.id as string);

				if (
					typeof generateSettings.postSermonSongPosition === 'number'
				) {
					const postSermonSongIndex =
						generateSettings.postSermonSongPosition - 1;

					if (inRange(postSermonSongIndex, oldIndex, newIndex)) {
						if (postSermonSongIndex === oldIndex) {
							setGenerateSettings((settings) => ({
								...settings,
								postSermonSongPosition: newIndex + 1,
							}));
						} else if (oldIndex > newIndex) {
							setGenerateSettings((settings) => ({
								...settings,
								postSermonSongPosition:
									(settings.postSermonSongPosition as number) +
									1,
							}));
						} else if (oldIndex < newIndex) {
							setGenerateSettings((settings) => ({
								...settings,
								postSermonSongPosition:
									(settings.postSermonSongPosition as number) -
									1,
							}));
						}
					}
				}

				return arrayMove(items, oldIndex, newIndex);
			});
		}
	};

	const onWarningClick = (warning: React.ReactNode) => {
		setIsWarningOpen(warning);
	};

	const handleDropdownOpen = (e: SyntheticEvent) => {
		setAnchorEl(e.currentTarget);
	};
	const handleDropdownClose = () => {
		setAnchorEl(null);
	};

	const noSongs = !selectedSongIds.length;
	const biblePassageAnalysis = biblePassageData?.getBiblePassageAnalysis;
	const isLoading = isGenerateLoading;
	const isInitialLoading =
		generateOnOpen &&
		(isBibleVerseLoading || isGenerateLoading) &&
		!selectedSongIds.length;
	const open = !!anchorEl;
	const postSermonSongId = !!generateSettings.postSermonSongPosition
		? selectedSongIds[generateSettings.postSermonSongPosition - 1]
		: undefined;

	const { findTeamSongsById } = useTeamSongs();
	const postSermonSongName = postSermonSongId
		? findTeamSongsById([postSermonSongId])[0]?.name
		: undefined;

	const sortableItems = selectedSongIds.map((sid, sindex) => {
		const songOverride = songOverrides.find((so) => so.id === sid);
		const isLocked = lockedTeamSongIds.includes(sid);
		const songReport = setlist?.feedback?.teamSongFeedbackReports.find(
			(r) => r.teamSongId === sid,
		);
		const score =
			status === SetlistStatusEnum.IN_REVIEW ||
			status === SetlistStatusEnum.COMPLETE
				? songReport?.meanTotalSentimentScore != null
					? Math.round(
							(songReport.meanTotalSentimentScore +
								Number.EPSILON) *
								10,
					  ) / 10
					: null // null = show dash (IN_REVIEW/COMPLETE but no feedback for this song)
				: undefined; // undefined = hide (all other statuses)
		return (
			<SortableItem
				key={`small-song-card-${sid}`}
				id={sid}
				songOverride={songOverride}
				onClick={(song) => {
					openSong({
						teamSongId: song.id,
						viewOnly: false,
					});
				}}
				onSwitchClick={() => {
					openSonglistWithCallback(
						(songIds, navigatedAway) => {
							if (songIds.length) {
								const nextIds = selectedSongIds.map((ssid) => {
									return ssid === sid ? songIds[0] : ssid;
								});
								setSelectedSongIds(nextIds);
								commitEditVersion({
									selectedSongIds: nextIds,
									totalSongPositions:
										generateSettings.totalSongPositions,
									postSermonSongPosition:
										generateSettings.postSermonSongPosition,
								});
								fireAddSongsEvent();
							}
							if (navigatedAway) {
								handleClose({});
							}
						},
						{
							actionLabelVerb: 'Switch',
							title: 'Switch song',
							excludeTeamSongIds: selectedSongIds,
							selectOnlyOne: true,
							noIndex: true,
						},
					);
				}}
				onLockClick={() => {
					setLockedTeamSongIds((state) => {
						if (state.includes(sid)) {
							return state.filter((id) => id !== sid);
						}
						return [...state, sid];
					});
				}}
				isLocked={isLocked}
				isLoading={isLocked ? false : isLoading}
				onWarningClick={onWarningClick}
				onViewAnalysisClick={() => {
					setSelectedTab(1);
				}}
				onAddPassageClick={() => {
					setSelectedTab(1);
					setIsPassagePanelOpen(true);
				}}
				onDefaultKeyClick={() => {
					setDefaultKeyPanelOpen({ teamSongId: sid });
				}}
				isPostSermon={
					generateSettings.postSermonSongPosition === sindex + 1
				}
				reference={generateSettings.bibleReference}
				isEdit={isEdit}
				isReorderMode={isReorderMode}
				onReorderClick={() => {
					reorderEntrySnapshotRef.current = currentSnapshot();
					fireEnterReorderMode();
					setIsReorderMode(true);
				}}
				score={score}
				displayKey={status !== SetlistStatusEnum.COMPLETE}
			/>
		);
	});

	return (
		<GenerateSetlistContext.Provider
			value={{
				open,
				anchorEl,
				selectedSongIds,
				songOverrides,
				isGenerateLoading,
				setlistId,
				isEdit,
				isReorderMode,
				setlist,
				status,
				lockedTeamSongIds,
				generateSettings,
				fromOnboarding,
				versionLabel,
				canUndo,
				canRedo,
				setFeedbackSetlistId,
				setIsSettingsPanelOpen,
				handleDropdownClose,
				generateSetlist,
				saveDraftSetlist,
				setIsEdit,
				setIsReorderMode,
				handleDeleteDialogOpen,
				setShareOpen,
				duplicateSetlist,
				undoVersion,
				redoVersion,
			}}
		>
			<AsaphDialog
				className={styles['root']}
				open={true}
				onClose={() => handleClose({})}
			>
				<div className={styles['toolbar']}>
					<AsaphDialogHeader
						leadingButton={{
							needsFadeIn: true,
							fadeTimeout: PANEL_TRANSITION_TIMEOUT,
							component: (
								<AsaphDialogHeaderButton
									type="text"
									label={isEdit ? 'Cancel' : 'Done'}
									handlePress={() => handleClose({})}
									className={classNames([
										fromOnboarding &&
											submittedFeedback &&
											'flashy',
									])}
								/>
							),
						}}
						title={{
							needsFadeIn: true,
							fadeTimeout: PANEL_TRANSITION_TIMEOUT,
							component: (
								<Flex
									justifyContent="center"
									alignItems="center"
									gap={5}
								>
									<SetlistSummaryCalendar
										startAtISO={generateSettings.startDate}
										size="sm"
									/>
									<SetlistServiceTitle
										className={styles['service-title']}
										serviceId={generateSettings.serviceId}
									/>
								</Flex>
							),
						}}
						trailingButton={{
							needsFadeIn: true,
							fadeTimeout: PANEL_TRANSITION_TIMEOUT,
							// ctaMode intentionally omitted — Give Feedback CTA lives in the bottom action bar
							component: (
								<SetlistStatus
									setlistId={setlistId}
									isEdit={isEdit}
								/>
							),
						}}
					/>
				</div>
				<div className={styles['toolbar-divider']}>
					<hr />
				</div>
				<section className={styles['content']}>
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragStart={() => {
							setIsDragging(true);
							Haptics.impact({ style: ImpactStyle.Medium });
						}}
						onDragEnd={handleDragEnd}
						modifiers={[
							restrictToVerticalAxis,
							restrictToParentElement,
						]}
					>
						<BottomActionLayout
							contentClassName={classNames([
								styles['generate-root'],
							])}
							actionContentClassName={
								styles['bottom-action-content']
							}
							actionDivider
							noPadding
							isScrollable={!isDragging}
							onBottomSheetClose={() => setIsWarningOpen('')}
							actionContent={
								<div className={styles['action-content']}>
									{hasPermission(Permissions.TeamUpdate) &&
										!!setlistId &&
										!isReorderMode && (
											<IconButton
												onClick={handleDropdownOpen}
												className={classNames([
													styles['more-button'],
												])}
											>
												<SvgIcon
													className={styles['icon']}
													component={more}
												/>
											</IconButton>
										)}

									<ActionContent />
								</div>
							}
						>
							{isEdit && (
								<SetlistChipRow
									serviceId={generateSettings.serviceId}
									bibleReference={
										generateSettings.bibleReference
									}
									totalSongPositions={
										generateSettings.totalSongPositions
									}
									postSermonSongPosition={
										generateSettings.postSermonSongPosition
									}
									disabled={isReorderMode}
									onServiceClick={() =>
										setIsServicePanelOpen(true)
									}
									onSongsClick={() =>
										setIsSongsPanelOpen(true)
									}
									onPassageClick={() =>
										setIsPassagePanelOpen(true)
									}
								/>
							)}
							<div
								className={classNames([
									styles['add-songs-area'],
									isDragging &&
										isCapacitor &&
										styles['disable-scroll'],
								])}
							>
								<div className={styles['add-songs-wrapper']}>
									{hasPermission(
										Permissions.DevtoolsRead,
									) && (
										<div
											className="my-sm mx-sm font-sm"
											onClick={() => {
												console.log(setlist);
											}}
										>
											Setlist id: {setlistId}
										</div>
									)}
									<div
										className={classNames([
											styles['song-card-list'],
										])}
									>
										{/* {isLoading ? (
												[
													...Array(
														generateSettings.totalSongPositions,
													).keys(),
												].map((i) => (
													<SmallSongCardButton
														isLoading={true}
													/>
												))
											) : ( */}
										<SortableContext
											items={selectedSongIds}
											strategy={
												verticalListSortingStrategy
											}
										>
											{sortableItems}
										</SortableContext>
										{/* )} */}
									</div>
									{isEdit && (
										<div
											className={
												styles['reorder-done-row']
											}
										>
											{isReorderMode ? (
												<SlotActionRow
													label="Reorder"
													primaryAction={{
														label: 'Done',
														onClick: () => {
															const entry =
																reorderEntrySnapshotRef.current;
															const now =
																currentSnapshot();
															if (
																entry &&
																!areSnapshotsEqual(
																	entry,
																	now,
																)
															) {
																commitEditVersion(
																	now,
																);
															}
															reorderEntrySnapshotRef.current =
																null;
															fireExitReorderMode();
															setIsReorderMode(
																false,
															);
														},
													}}
												/>
											) : (
												<SlotActionRow
													label={`Version ${versionLabel}`}
													middleContent={
														<>
															<button
																type="button"
																className={
																	styles['version-icon-button']
																}
																aria-label="Undo"
																disabled={!canUndo}
																onClick={undoVersion}
															>
																<SvgIcon
																	component={undoIcon}
																	viewBox="0 -0.83 20 20"
																	className={
																		styles['version-icon']
																	}
																/>
															</button>
															<button
																type="button"
																className={
																	styles['version-icon-button']
																}
																aria-label="Redo"
																disabled={!canRedo}
																onClick={redoVersion}
															>
																<SvgIcon
																	component={redoIcon}
																	viewBox="0 -0.83 20 20"
																	className={
																		styles['version-icon']
																	}
																/>
															</button>
														</>
													}
													primaryAction={{
														label: 'Generate',
														color: 'gradient',
														onClick: () =>
															generateSetlist(),
														disabled:
															isGenerateLoading ||
															lockedTeamSongIds.length >=
																generateSettings.totalSongPositions,
													}}
												/>
											)}
										</div>
									)}
								</div>
							</div>
							<Tabs
								className={styles['tabs']}
								contentClassName={styles['tabs-content']}
								selectedIndex={selectedTab}
								onChange={(index) => {
									const TAB_NAMES = [
										'Analysis',
										'Sermon',
										'Reports',
									];
									fireViewTab({
										tab: TAB_NAMES[index] ?? String(index),
									});
									setSelectedTab(index);
								}}
								stickyBar
								items={[
									{ title: 'Analysis' },
									{ title: 'Sermon' },
									{ title: 'Reports' },
								]}
								renderContent={({ selectedIndex }) => {
									if (selectedIndex === 0) {
										return (
											<ModalSectionCard
												borderRadiusSize="sm"
												paddingSize="md"
											>
												<SetlistSummary
													teamSongIds={
														selectedSongIds
													}
												/>
											</ModalSectionCard>
										);
									}
									if (selectedIndex === 1) {
										return !generateSettings.bibleReference ? (
											<ModalSectionCard
												borderRadiusSize="sm"
												paddingSize="md"
											>
												<ModalSectionTitle centered>
													{postSermonSongName ? (
														<>
															Analyze{' '}
															{postSermonSongName}
															<br />
															with your sermon
															passage
														</>
													) : (
														'Add your passage to get sermon analysis'
													)}
												</ModalSectionTitle>
												<AsaphButton
													color="grey"
													label="Add Passage"
													className="mt-sm"
													fullWidth
													onClick={() =>
														setIsPassagePanelOpen(
															true,
														)
													}
												/>
											</ModalSectionCard>
										) : (
											<ModalSectionCard
												borderRadiusSize="sm"
												paddingSize="0"
											>
												<SongAnalysis
													bibleReference={
														generateSettings.bibleReference
													}
													teamSongId={
														postSermonSongId!
													}
												/>
											</ModalSectionCard>
										);
									}
									if (selectedIndex === 2) {
										return (
											<SetlistAnalysis
												variant="list"
												isLoading={isLoading}
												biblePassageAnalysis={
													biblePassageAnalysis
												}
												reference={
													generateSettings.bibleReference
												}
												teamSongIds={selectedSongIds}
												postSermonSongPosition={
													generateSettings.postSermonSongPosition
												}
											/>
										);
									}
								}}
							/>
						</BottomActionLayout>
					</DndContext>
				</section>
			</AsaphDialog>

			<ActionSheet
				open={isDeleteOpen}
				title="Are you sure?"
				onClose={handleDeleteDialogClose}
			>
				<ActionSheetAction
					label="Yes, delete setlist"
					onActionClick={deleteSetlist}
					clickContext={{
						handleDialogClose: handleDeleteDialogClose,
					}}
					type="danger"
				/>
			</ActionSheet>

			{isSettingsPanelOpen && (
				<GenerateSettingsBottomPanel
					hasCloseButton
					onClose={() => setIsSettingsPanelOpen(false)}
					saveLabel="Save settings"
					saveColor="blue"
					title="Generate Settings"
					hasGenerate={false}
					generateSettings={generateSettings}
					reasoning={reasoning}
					lastGeneratePositions={lastGeneratePositions}
					setAccountOpen={setAccountOpen}
					onSave={(settings) => {
						setGenerateSettings(settings);
						setIsSettingsPanelOpen(false);
					}}
				/>
			)}

			{feedbackSetlistId && (
				<FeedbackPanel
					onClose={({ submittedFeedback }) => {
						setFeedbackSetlistId(null);
						if (submittedFeedback) {
							setSubmittedFeedback(true);
						}
						if (submittedFeedback && fromOnboarding) {
							openBottomCard?.({
								title: 'Setlist Feedback',
								text: 'Thank you for providing feedback on your setlist!',
							});
						}
					}}
					config={{
						setlistId: feedbackSetlistId,
						noToast: fromOnboarding,
					}}
				/>
			)}

			{shareOpen && <ShareDialog onClose={() => setShareOpen(false)} />}

			{isServicePanelOpen && (
				<ServiceChipPanel
					initialServiceId={generateSettings.serviceId}
					initialStartDate={generateSettings.startDate}
					initialStartTime={generateSettings.startTime}
					onSave={({ serviceId, startDate, startTime }) => {
						setGenerateSettings((s) => ({
							...s,
							serviceId,
							startDate,
							startTime,
						}));
						setIsServicePanelOpen(false);
					}}
					onClose={() => setIsServicePanelOpen(false)}
					setAccountOpen={setAccountOpen}
				/>
			)}

			{isSongsPanelOpen &&
				(() => {
					const postPos = generateSettings.postSermonSongPosition;
					const preSlots =
						typeof postPos === 'number'
							? postPos - 1
							: generateSettings.totalSongPositions;
					const postIds =
						typeof postPos === 'number'
							? selectedSongIds.slice(postPos - 1)
							: [];
					const currentPreSongCount = selectedSongIds
						.slice(0, preSlots)
						.filter((id) => !id.startsWith('song-')).length;
					const currentPostSongCount = postIds.filter(
						(id) => !id.startsWith('song-'),
					).length;
					const initialPre =
						typeof postPos === 'number'
							? postPos - 1
							: generateSettings.totalSongPositions;
					const initialPost =
						typeof postPos === 'number'
							? generateSettings.totalSongPositions -
							  (postPos - 1)
							: 0;
					return (
						<SongsChipPanel
							initialPre={initialPre}
							initialPost={initialPost}
							currentPreSongCount={currentPreSongCount}
							currentPostSongCount={currentPostSongCount}
							onSave={({
								totalSongPositions,
								postSermonSongPosition,
							}) => {
								const nextIds = [...selectedSongIds];
								if (nextIds.length > totalSongPositions) {
									nextIds.length = totalSongPositions;
								}
								while (nextIds.length < totalSongPositions) {
									nextIds.push(
										`song-${nextIds.length + 1}`,
									);
								}
								const nextSnapshot: SetlistVersionSnapshot = {
									selectedSongIds: nextIds,
									totalSongPositions,
									postSermonSongPosition,
								};
								const changed = !areSnapshotsEqual(
									currentSnapshot(),
									nextSnapshot,
								);
								setSelectedSongIds(nextIds);
								setGenerateSettings((s) => ({
									...s,
									totalSongPositions,
									postSermonSongPosition,
								}));
								commitVersion(nextSnapshot);
								if (changed) {
									markDirty();
								}
								setIsSongsPanelOpen(false);
							}}
							onClose={() => setIsSongsPanelOpen(false)}
						/>
					);
				})()}

			{isPassagePanelOpen && (
				<PassageChipPanel
					initialBibleReference={generateSettings.bibleReference}
					onSave={(bibleReference) => {
						setGenerateSettings((s) => ({ ...s, bibleReference }));
						setIsPassagePanelOpen(false);
					}}
					onClose={() => setIsPassagePanelOpen(false)}
				/>
			)}

			{defaultKeyPanelOpen && (
				<DefaultKeyBottomCard
					teamSongId={defaultKeyPanelOpen.teamSongId}
					songOverrides={songOverrides}
					onSave={(defaultKey) => {
						setSongOverrides((overrides) => {
							const filtered = overrides.filter(
								(override) =>
									!(
										override.id ===
										defaultKeyPanelOpen.teamSongId
									),
							);

							if (defaultKey === undefined) {
								return filtered;
							}

							return [
								...filtered,
								{
									id: defaultKeyPanelOpen.teamSongId,
									defaultKey,
								},
							];
						});
						setDefaultKeyPanelOpen(null);
					}}
					onClose={() => setDefaultKeyPanelOpen(null)}
				/>
			)}
		</GenerateSetlistContext.Provider>
	);
};

const RotationStatusWarning: React.FC<{ warning: React.ReactNode }> = ({
	warning,
}) => {
	return (
		<div className={styles['warning-sheet']}>
			<div className={styles['title']}>Rotation Status Warning</div>
			<div className={styles['body']}>{warning}</div>
		</div>
	);
};

export const generatePositions = ({
	totalPositionsNumber,
	postSermonSongPosition,
	searchTerm,
	bibleReference,
	lockedTeamSongIds,
	currentTeamSongIds,
}: {
	totalPositionsNumber: number;
	postSermonSongPosition?: number | null;
	searchTerm?: string;
	bibleReference?: string;
	lockedTeamSongIds?: string[];
	currentTeamSongIds?: string[];
}): GenerateSetlistRequestInput[] => {
	let basePostions: {
		energyTags?: string[];
		gospelTags?: string[];
		rotationStatus?: string[];
		searchTerm?: string;
		bibleReference?: string;
	}[] = [];

	switch (totalPositionsNumber) {
		case 3:
			basePostions = [
				{
					energyTags: ['upbeat', 'building'],
					gospelTags: ['glory of god'],
					rotationStatus: ['!seasonal'],
				},
				{
					energyTags: ['reflective', 'settling'],
					gospelTags: ['gravity of sin'],
					rotationStatus: ['!seasonal'],
				},
				{
					energyTags: ['building', 'upbeat'],
					gospelTags: ['grandeur of grace'],
					rotationStatus: ['!seasonal'],
				},
			];
			break;
		case 4:
			basePostions = [
				{
					energyTags: ['upbeat', 'bulding'],
					gospelTags: ['glory of god'],
					rotationStatus: ['!seasonal'],
				},
				{
					energyTags: ['settling', 'building'],
					gospelTags: ['gravity of sin'],
					rotationStatus: ['!seasonal'],
				},
				{
					energyTags: ['reflective', 'settling'],
					rotationStatus: ['!seasonal'],
				},
				{
					energyTags: ['building', 'upbeat'],
					gospelTags: ['grandeur of grace'],
					rotationStatus: ['!seasonal'],
				},
			];
			break;
		default:
			for (let i = 0; i < totalPositionsNumber; i++) {
				basePostions.push({
					energyTags: ['upbeat', 'building'],
					gospelTags: ['glory of god'],
					rotationStatus: ['!seasonal'],
				});
			}
	}

	return basePostions.map((position, i) => {
		const currentTeamSongId = currentTeamSongIds?.[i];
		const teamSongId =
			lockedTeamSongIds?.includes(currentTeamSongId || '') &&
			currentTeamSongId;
		return {
			...position,
			searchTerm:
				i + 1 === postSermonSongPosition ? searchTerm : undefined,
			isPostSermon: i + 1 === postSermonSongPosition,
			bibleReference:
				i + 1 === postSermonSongPosition ? bibleReference : undefined,
			teamSongId: teamSongId || undefined,
		};
	});
};
